<template>
  <div :class="mapContainerClass" id="mapSVG"></div>
</template>

<script>
import moment from 'moment'
import * as d3 from 'd3'
import Rainbow from 'rainbowvis.js'
import { D3Map } from '../../utils/D3Map'
import sitesLevelsMap from '../../assets/config/sites_levels_map.json'
import { utilsMixin } from '../../utils/mixins'

export default {
  name: 'SVGMap',
  mixins: [utilsMixin],
  dependencies: ['adminService'],
  props: ['dataset', 'routeForm', 'startDate', 'endDate', 'colorSpectrum'],
  data() {
    return {
      timeout: null,
      d3Map: null,
      canvasWidth: 1280,
      canvasHeight: 720,
      fullScreen: false,
      svg: null,
      colorRainbow: new Rainbow(),
      levelsSVG: [],
      cssClass: [
        'background',
        'wall',
        'wall-alt',
        'path',
        'path-alt',
        'visited-indoor',
        'visited-indoor-alt',
        'visited-outdoor-alt',
        'underground',
        'underground-alt',
        'visitable-indoor',
        'visitable-indoor-alt',
        'visitable-outdoor',
        'visitable-outdoor-alt',
        'alarm-outdoor',
        'alarm-outdoor-alt',
        'alarm-indoor',
        'alarm-indoor-alt',
        'closed-indoor',
        'closed-indoor-alt',
        'closed-outdoor',
        'closed-outdoor-alt',
        'water',
        'water-alt',
        'grass',
        'grass-alt',
        'silhouette',
        'silhouette-alt',
      ],
    }
  },
  watch: {
    dataset: {
      handler: function() {
        this.loadSVGMap()
          .then(() => {
            setTimeout(() => {
              this.displayMapData()
            }, 500)
          })
          .catch(error => {
            console.error('Error when loading map data:', error)
          })
      },
      immediate: true,
    },
    routeForm: {
      handler: function() {
        this.loadSVGMap()
          .then(() => {
            setTimeout(() => {
              this.displayMapData()
            }, 500)
          })
          .catch(error => {
            console.error('Error when loading map data:', error)
          })
      },
      immediate: true,
    },
    startDate: {
      handler: function() {},
      immediate: true,
    },
    colorSpectrum: {
      handler: function() {
        this.updateColorSpectrum()
      },
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)

    this.updateColorSpectrum()

    this.adminService.getAllSitesWithMaps().then(data => {
      var resultSites = data.result
      if (this.getCurrentUser.role == 'partner') {
        resultSites = resultSites.filter(site => {
          var found = this.getCurrentUser.sites.find(userSite => {
            return site.site === userSite.site
          })
          return found ? true : false
        })
        if (!resultSites || resultSites.length == 0) {
          this.$router.replace('dashboard')
        }
      }

      this.$store.dispatch('config/setCurrentSitesWithMaps', resultSites).then(() => {
        this.loadMapData()
      })
    })
  },
  computed: {
    mapContainerClass: function() {
      return this.fullScreen
        ? {}
        : {
            'pb-4': true,
            'col-sm-9': this.isLargeScreen || this.isVeryLargeScreen,
            'col-sm-11': !this.isVeryLargeScreen,
          }
    },
    mapSiteIDs: function() {
      return [this.routeForm.site]
    },
    sites: function() {
      return sitesLevelsMap
    },
  },
  methods: {
    loadMapData: function() {
      //Use to load the map
      if (!this.sitesWithMaps || this.sitesWithMaps.length == 0) {
        return
      }

      if (this.getCurrentUser.role == 'partner') {
        var found = this.getCurrentUser.sites.find(userSite => {
          return this.routeForm.site == userSite.site_id
        })
        if (!found) {
          //If user is not attached to the selected/current site we update the current by his first site
          this.routeForm.site = this.getCurrentUser.sites[0].site_id
        } else {
          if (found.levels.split(',').indexOf(String(this.routeForm.level)) == -1) {
            this.routeForm.level = found.default_level
          }
        }
      } else {
        let found = this.sitesWithMaps.find(siteWithMap => {
          return this.routeForm.site == siteWithMap.site_id
        })
        if (found) {
          if (found.levels.split(',').indexOf(this.routeForm.level) == -1) {
            this.routeForm.level = found.default_level
          }
        }
      }

      //We load the SVG map
      this.loadSVGMap().catch(error => {
        console.error('Error when loading map data:', error)
      })
    },
    loadSVGMap: function() {
      //Use to load the SVG file of the selected map
      return new Promise((resolve, reject) => {
        d3.selectAll('#mapSVG svg').remove()

        /*let svgPath =
          this.routeForm.logVersionMajor >= 4 && this.routeForm.logVersionMinor >= 2
            ? `/maps/levels_${this.routeForm.site}.svg`
            : `/maps/${this.routeForm.site}/levels/${this.routeForm.level}/level_map.svg`*/

        let svgPath = `/maps/map_${this.routeForm.site}.svg`

        this.d3Map = new D3Map(this.canvasWidth, this.canvasHeight)
        this.d3Map.setDataset(this.dataset)
        this.d3Map.canvas(d3.select('#mapSVG'))
        this.d3Map
          .loadSVG(svgPath)
          .then(() => {
            if (this.getCurrentUser.role == 'partner') {
              let found = this.getCurrentUser.sites.find(s => {
                return s.site == this.routeForm.site
              })

              if (!found) {
                this.routeForm.site = this.getCurrentUser.sites[0].site
              }
            }

            //Prepare SVG file css class for we used in logs 4.2
            if (this.routeForm.logVersionMajor >= 4 && this.routeForm.logVersionMinor >= 2) {
              this.levelsSVG = this.selectSVGElements('level')
              this.prepareSVGClass()
              //Display the selected level
              this.displaySelectedLevel()
            }

            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    displayMapData: function() {
      //Display map data according to the fetched dataset
      let max = 0
      let min = 0

      this.dataset.routes.forEach(area => {
        if (max < area.value) max = area.value
        if (min > area.value) min = area.value
      })

      if (min == max) {
        max = min + 1
      }

      this.updateColorSpectrum()
      this.colorRainbow.setNumberRange(min, max)

      console.debug('displayMapData with range', min, max)

      d3.select('#mapSVG svg').attr('id', 'svg-mapchart')
      d3.selectAll('.area-infos').remove()
      d3.selectAll('#mapSVG .svgArea').attr('fill', '#A3BFCE')

      const paddingLeftRight =
        Number(this.dataset.site_location) === 'deutschland.moritzburg.schlossmoritzburg' ? 2 : 10
      const paddingTopBottom =
        Number(this.dataset.site_location) === 'deutschland.moritzburg.schlossmoritzburg' ? 1 : 3
      const fontSize =
        Number(this.dataset.site_location) === 'deutschland.moritzburg.schlossmoritzburg'
          ? '5px'
          : '20px'
      const offset =
        Number(this.dataset.site_location) === 'deutschland.moritzburg.schlossmoritzburg' ? 8 : 23

      //On over behaviour use to highlight the selected area

      let svgAreaOnOver = function() {
        let el = d3.select(this)
        d3.selectAll('.area-infos').style('opacity', '0')
        d3.select('#placeAreaText-' + el.attr('id')).style('opacity', 1)
        d3.select('#placeValueText-' + el.attr('id')).style('opacity', 1)
        d3.select('#placeAreaRect-' + el.attr('id')).style('opacity', 0.4)
        d3.select('#placeValueRect-' + el.attr('id')).style('opacity', 0.4)

        if (el.attr('id').startsWith('place')) {
          let splits = el.attr('id').split('-')
          let areaID = ''
          if (splits.length == 3) {
            areaID = splits[1] + '-' + splits[2]
          } else if (splits.length == 2) {
            areaID = splits[1]
          }
          d3.select('#placeAreaText-' + areaID).style('opacity', 1)
          d3.select('#placeValueText-' + areaID).style('opacity', 1)
          d3.select('#placeAreaRect-' + areaID).style('opacity', 0.4)
          d3.select('#placeValueRect-' + areaID).style('opacity', 0.4)
        }
      }

      let svgAreaOnOut = function() {
        d3.selectAll('.infos-rect').style('opacity', 0.4)
        d3.selectAll('.infos-text').style('opacity', 1)
      }

      this.dataset.routes = this.dataset.routes.filter(route => {
        return route.area_id !== '' && route.area_name !== '' && route.area_name
      })

      this.dataset.routes.forEach(area => {
        if (this.routeForm.logVersionMajor >= 4 && this.routeForm.logVersionMinor >= 2) {
          //for logs 4.2
          area.raw_area_name = area.area_id
          area.area_name = area.area_name.replace(/\./g, '_').replace('+', '')
        } else {
          area.raw_area_name = area.area_id
          area.area_name = area.area_name.replace(/\./g, '').replace('+', '')
        }

        console.debug('Found area name in data', area.area_name, area.raw_area_name)

        let svgArea
        if (this.routeForm.logVersionMajor >= 4 && this.routeForm.logVersionMinor >= 2) {
          //for logs 4.2
          let selectedAreas = this.selectSVGElements(area.area_name)
          console.debug('Found selected area', selectedAreas, area.area_name)
          if (!selectedAreas || selectedAreas.length == 0) {
            return
          }

          //Group selected areas (and his alternatives) to a single one svg area, easier to work with
          let groupedAreas

          selectedAreas.forEach((svgArea, index) => {
            if (index == 0) {
              groupedAreas = svgArea
            } else {
              //Copying child nodes
              svgArea.selectAll('path').each(function() {
                let node = d3.select(this)
                groupedAreas.append('path').attr('d', node.attr('d'))
              })
              svgArea.remove()
            }
          })

          svgArea = groupedAreas
        } else {
          svgArea = d3.select('#' + area.area_name)
        }

        console.debug('Found SVG AREA', svgArea)

        if (!svgArea.empty()) {
          let g = svgArea.select(function() {
            return this.parentNode.parentNode //Getting "level" parent node
          })

          g.attr('class', 'area-found')
          let areaRect = g.append('rect')
          let valueRect = g.append('rect')

          let areaText = g
            .append('text')
            .attr('x', svgArea.node().getBBox().x + svgArea.node().getBBox().width / 3)
            .attr('y', svgArea.node().getBBox().y + svgArea.node().getBBox().height / 3)
            .attr('fill', 'white')
            .attr('text-anchor', 'middle')
            .attr('font-size', fontSize)
            .attr('class', 'area-infos infos-text')
            .attr('id', 'placeAreaText-' + area.area_name)
            .attr('area-id', area.raw_area_name)
            .text(
              // area.raw_area_name,
              this.$i18n.messages[this.$i18n.locale]['map_areas'][this.routeForm.site][
                area.raw_area_name
              ],
            )

          let bb = areaText.node().getBBox()

          areaRect
            .attr('x', bb.x - paddingLeftRight)
            .attr('y', bb.y - paddingTopBottom)
            .attr('width', bb.width + paddingLeftRight * 2)
            .attr('height', bb.height + paddingTopBottom * 2)
            .attr('class', 'area-infos infos-rect')
            .attr('area-id', area.raw_area_name)
            .attr('id', 'placeAreaRect-' + area.area_name)
            .style('opacity', 0.4)
            .style('fill', 'black')

          let value
          switch (this.routeForm.dataType) {
            case 'average_time':
            case 'max_time':
            case 'min_time':
            case 'duration':
              value = moment
                .utc(moment.duration(area.value, 'seconds').asMilliseconds())
                .format(area.value > 3600 ? 'hh:mm:ss' : 'mm:ss')
              break
            case 'count':
            case 'session_count':
              value = area.value
              break
            default:
              value = area.value
              break
          }

          let valueText = g
            .append('text')
            .attr('x', svgArea.node().getBBox().x + svgArea.node().getBBox().width / 3)
            .attr('y', svgArea.node().getBBox().y + svgArea.node().getBBox().height / 3 + offset)
            .attr('fill', 'white')
            .attr('text-anchor', 'middle')
            .attr('font-size', fontSize)
            .attr('class', 'area-infos infos-text')
            .attr('area-id', area.raw_area_name)
            .attr('id', 'placeValueText-' + area.area_name)
            .text(value)

          bb = valueText.node().getBBox()

          valueRect
            .attr('x', bb.x - paddingLeftRight)
            .attr('y', bb.y - paddingTopBottom)
            .attr('width', bb.width + paddingLeftRight * 2)
            .attr('height', bb.height + paddingTopBottom * 2)
            .attr('class', 'area-infos infos-rect')
            .attr('area-id', area.raw_area_name)
            .attr('id', 'placeValueRect-' + area.area_name)
            .style('opacity', 0.4)
            .style('fill', 'black')

          svgArea.attr('class', null)
          svgArea.attr('class', 'svgArea')
          svgArea.attr(
            'fill',
            area.value > 0 ? '#' + this.colorRainbow.colorAt(area.value) : '#5E5E5E',
          )

          svgArea.on('mouseover', svgAreaOnOver)
          svgArea.on('mouseout', svgAreaOnOut)

          svgArea.selectAll('polygon').attr('class', null)
        }
      })

      d3.selectAll('.area-infos')
        .on('mouseover', svgAreaOnOver)
        .on('mouseout', svgAreaOnOut)
      /*.on("click", function(d) {
          let areaID =  d3.select(this).attr("area-id");
          let url = `/areas/site/${that.routeForm.site}/area/${areaID}/start/${that.startDate}/end/${that.endDate}`
          Object.assign(document.createElement('a'), { target: '_blank', href: url}).click();
        });*/
    },
    displaySelectedLevel: function() {
      let that = this
      this.levelsSVG.forEach(function(d) {
        let el = d
        let level = el.attr('id').split('_')[1]
        if (String(that.routeForm.level) == String(level)) {
          el.style('display', 'block')
        } else {
          el.style('display', 'none')
        }
      })
    },
    prepareSVGClass: function() {
      //Removing all area colors from the initial SVG file
      d3.select('#mapSVG')
        .selectAll('path')
        .attr('fill', '')

      //Adding preset css class to each grouped areas (background, wall, visitable etc.)
      this.cssClass.forEach(cls => {
        let areaElements = this.selectSVGElements(cls)
        areaElements.forEach(el => {
          el.attr('class', cls)
        })
      })
    },
    selectSVGElements: function(prefixID) {
      //Return SVG nodes according to his prefix ID
      let selectedEls = []
      d3.selectAll('#mapSVG g').each(function() {
        var el = d3.select(this)

        if (el && String(el.attr('id')).startsWith(prefixID)) {
          selectedEls.push(el)
        }
      })
      return selectedEls
    },
    updateColorSpectrum() {
      if (this.colorSpectrum && this.colorSpectrum.length > 1) {
        this.colorRainbow.setSpectrum(this.colorSpectrum[0], this.colorSpectrum[1])
      } else if (this.colorSpectrum && this.colorSpectrum.length == 1) {
        this.colorRainbow.setSpectrum(this.colorSpectrum[0], this.colorSpectrum[0])
      } else {
        this.colorRainbow.setSpectrum('#FFF858', '#FF6B6B')
      }
    },
  },
}
</script>

<style>
.area-infos {
  z-index: 10;
}

.infos-text {
  font-weight: 600;
}

/* Color used for unused map zones */
.cls-1 {
  fill: #0b3d5f;
}

.cls-2 {
  fill: #a3bfce;
}

/* Map */
.canvas {
  overflow: hidden;
}

.canvas .wrapper.outer > .background-map {
  fill: #000000;
}

.canvas .wrapper.inner > .background-map {
  fill: #2e4a57;
  cursor: move;
}

.canvas .background-map {
  /*fill: #F6F6F6;*/
  fill: #2e4a57;
  /*stroke: #333333;*/
  cursor: move;
}

.canvas .panCanvas {
  cursor: move;
}

.canvas .minimap .frame {
  pointer-events: all;
}
.canvas .minimap .frame .background-map {
  stroke: #111111;
  stroke-width: 4px;
  fill-opacity: 0.1;
  fill: #000000;
  fill: url(#minimapGradient_qwpyza);
  filter: url(#minimapDropShadow_qwpyza);
  cursor: move;
}

/** CLASS USE FOR AREAS **/

.path {
  fill: #a6a892;
}

.background {
  fill: #6d7b8d;
}

.wall {
  fill: #272b32;
}

.wall-alt {
  fill: #363a40;
}

.path {
  fill: #a6a892;
}

.path-alt {
  fill: #999b84;
}

.visited-indoor {
  fill: #409a6c;
}

.visited-indoor-alt {
  fill: #409a6c;
}

.visited-outdoor {
  fill: #85d1ac;
}

.visited-outdoor-alt {
  fill: #72bf9a;
}

.underground {
  fill: #595242;
}

.underground-alt {
  fill: #514a3a;
}

.visitable-indoor {
  fill: #7490a7;
}

.visitable-indoor-alt {
  fill: #658299;
}

.visitable-outdoor {
  fill: #92adc4;
}

.visitable-outdoor-alt {
  fill: #8aa1b4;
}

.alarm-outdoor {
  fill: #ae9da4;
}

.alarm-outdoor-alt {
  fill: #aa8b91;
}

.alarm-indoor {
  fill: #8a6268;
}

.alarm-indoor-alt {
  fill: #7c535a;
}

.closed-indoor {
  fill: #4a4e54;
}

.closed-indoor-alt {
  fill: #5a5e64;
}

.closed-outdoor {
  fill: #979ba2;
}

.closed-outdoor-alt {
  fill: #8d9197;
}

.water {
  fill: #8c9aa4;
}

.water-alt {
  fill: #8194a0;
}

.grass {
  fill: #76938b;
}

.grass-alt {
  fill: #64897c;
}

.silhouette {
  fill: #616f83;
}

.silhouette-alt {
  fill: #5b687b;
}
</style>
